import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, TrendingUp, Loader2, Lock, Check, LogIn, Calendar, Ticket, IdCard, ClipboardList, QrCode, Mail, Globe, Download } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { LOGO_URL } from "@/lib/content";
import BookDemoSection from "@/components/BookDemoSection";

const TICKET_PRICE = 40;                     // Basis for the "what we save you" calculation
const STRIPE_RATE = 0.015;                    // Stripe UK domestic %
const STRIPE_FIXED = 0.20;                    // Stripe UK domestic fixed
const STRIPE_FEE_PER_TICKET = TICKET_PRICE * STRIPE_RATE + STRIPE_FIXED; // £0.80 per £40 ticket
const VENUE_BONUS_PER_TICKET = 0.20;          // Flat 20p bonus per ticket paid to venue
const SAVINGS_PER_TICKET = STRIPE_FEE_PER_TICKET + VENUE_BONUS_PER_TICKET; // £1.00 per ticket
const MIN = 200;
const MAX = 5000;
const STEP = 50;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatGBP = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(n);

const INITIAL_FORM = {
  venue_name: "",
  venue_website: "",
  first_name: "",
  last_name: "",
  email: "",
};

export default function Landing() {
  const [tickets, setTickets] = useState(1000);
  const monthly = useMemo(() => tickets * SAVINGS_PER_TICKET, [tickets]);
  const annual = monthly * 12;

  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.venue_name || !form.first_name || !form.last_name || !form.email) {
      toast.error("Please fill in all four fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        venue_name: form.venue_name.trim(),
        venue_website: form.venue_website.trim() || null,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
      };
      await axios.post(`${API}/info-pack`, payload);
      toast.success("Info pack on its way — check your email.");
      setForm(INITIAL_FORM);
      setSubmitted(true);
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="landing-page"
      className="min-h-screen bg-black text-[#F5F5F0] grain relative overflow-x-clip"
    >
      {/* MINIMAL HEADER */}
      <header className="sticky top-0 z-40 bg-black/85 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20 md:h-24">
          <Link to="/" data-testid="landing-logo-link" className="flex items-center">
            <img
              src={LOGO_URL}
              alt="Swingle For Venues"
              data-testid="landing-logo"
              className="h-14 md:h-16 w-auto object-contain"
            />
          </Link>
          <nav className="flex items-center gap-2 md:gap-4" aria-label="Primary">
            <Link
              to="/about"
              data-testid="landing-nav-about"
              className="hidden md:inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[#A3A3A3] hover:text-[#F5F5F0] transition-colors px-3 py-2"
            >
              About
            </Link>
            <a
              href="#contact"
              data-testid="landing-nav-contact"
              className="hidden md:inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[#A3A3A3] hover:text-[#F5F5F0] transition-colors px-3 py-2"
            >
              Get Our Venue Info Pack
            </a>
            <a
              href="#book-demo"
              data-testid="landing-nav-book-demo"
              className="hidden lg:inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[#A3A3A3] hover:text-[#F5F5F0] transition-colors px-3 py-2"
            >
              Book a demo
            </a>
            <Link
              to="/portal/login"
              data-testid="landing-nav-venue-login"
              className="inline-flex items-center gap-1.5 md:gap-2 border border-white/25 hover:border-[#D9A6F5] hover:text-[#D9A6F5] text-[#F5F5F0] px-3.5 md:px-5 py-2.5 text-[10px] md:text-[11px] uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <LogIn size={12} /> Venue Login
            </Link>
            <a
              href="#contact"
              data-testid="landing-nav-cta"
              className="hidden sm:inline-flex items-center gap-1.5 md:gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-4 md:px-5 py-2.5 text-[10px] md:text-[11px] uppercase tracking-[0.22em] font-bold transition-colors"
            >
              Get Our Venue Info Pack <ArrowUpRight size={12} />
            </a>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative z-[2] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-12 md:pb-20 text-center">
          <div className="inline-flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-[#D9A6F5]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#D9A6F5]">
              Sell Tickets To Late Night Events? You Need To Know About Us!
            </span>
            <span className="h-px w-10 bg-[#D9A6F5]" />
          </div>
          <h1
            data-testid="landing-headline"
            className="font-black tracking-[-0.04em] leading-[0.95] max-w-6xl mx-auto"
            style={{ fontSize: "clamp(2.25rem, 7vw, 6rem)" }}
          >
            The Only Platform You'll Ever Need. It Pays For Itself.{" "}
            <span className="font-accent text-[#D9A6F5] inline-block -rotate-2">
              And Then Some.
            </span>
          </h1>
          <p className="mt-8 max-w-2xl mx-auto text-lg md:text-xl text-[#A3A3A3] leading-relaxed">
            We replace four of the tools you&apos;re currently paying for whilst putting £100s
            extra in your account each month. Fewer outgoings, More Revenue, Zero Tech Headaches.
            What Are You Waiting For?
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="#contact"
              data-testid="landing-hero-cta"
              className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-8 py-4 text-xs uppercase tracking-[0.24em] font-bold transition-colors"
            >
              Get Our Venue Info Pack <ArrowUpRight size={14} />
            </a>
            <a
              href="#book-demo"
              data-testid="landing-hero-book-demo"
              className="inline-flex items-center gap-2 border border-white/25 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-8 py-4 text-xs uppercase tracking-[0.24em] font-bold transition-colors"
            >
              Book a Demo <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* NO PAYMENT PROCESSING FEES */}
      <section className="relative z-[2] border-b border-white/10 bg-black">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#D9A6F5]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#D9A6F5]">
              Zero processing fees
            </span>
            <span className="h-px w-10 bg-[#D9A6F5]" />
          </div>
          <h2
            data-testid="landing-no-fees-title"
            className="text-3xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[1]"
          >
            Say Goodbye To Payment Processing Fees.{" "}
            <span className="font-accent italic text-[#D9A6F5] inline-block -rotate-2">
              Say Hello To Extra Revenue.
            </span>
          </h2>
          <p
            data-testid="landing-no-fees-body"
            className="mt-8 max-w-2xl mx-auto text-lg md:text-xl text-[#A3A3A3] leading-relaxed"
          >
            Our unique pricing model shares booking fee revenue with you, we cover the cost of
            payment processing and give you an extra £0.20p on top,{" "}
            <span className="text-[#D9A6F5]">
              so you will absolutely always receive 100% of your ticket price plus £0.20p.
            </span>
          </p>
          <CtaCluster id="no-fees" />
        </div>
      </section>

      {/* WHAT WE REPLACE */}
      <section className="relative z-[2] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="text-center mb-14 md:mb-16">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D9A6F5]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#D9A6F5]">
                What We Replace
              </span>
              <span className="h-px w-10 bg-[#D9A6F5]" />
            </div>
            <h2 className="text-3xl md:text-5xl font-black tracking-tighter leading-[1] max-w-3xl mx-auto">
              Four platforms.{" "}
              <span className="font-accent text-[#D9A6F5] inline-block -rotate-2">Replaced Forever.</span>
            </h2>
            <p className="mt-6 text-lg text-[#A3A3A3] leading-relaxed max-w-2xl mx-auto">
              We replace a host of platforms you're currently using and drop £100s extra in your
              account each and every week. What are you waiting for?
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            {[
              {
                key: "ticketing",
                title: "Ticketing",
                brands: [
                  { name: "Ticket Tailor", slug: null },
                  { name: "Eventbrite", slug: "eventbrite" },
                  { name: "Skiddle", slug: null },
                ],
                cost: "~£500 / mo",
                costNote: "in booking fees paid to a 3rd party",
                body:
                  "Unlike Ticket Tailor, Eventbrite or Skiddle, we share booking-fee revenue with you, our pricing model covers the cost of payment processing and gives you a little bit extra on every ticket you sell — adding thousands back onto your bottom line each and every year, all without your customers having to pay anymore than they currently are.",
              },
              {
                key: "email-marketing",
                title: "Email Marketing",
                brands: [
                  { name: "MailChimp", slug: "mailchimp" },
                  { name: "MailerLite", slug: "mailerlite" },
                ],
                cost: "~£30 / mo",
                costNote: "typical mid-tier plan",
                body:
                  "Ditch MailChimp and MailerLite. We deliver all of your emails on autopilot — new events, ticket drops, weekly 'What's On', membership reminders and ticket delivery — all without you lifting a finger.",
              },
              {
                key: "websites",
                title: "Websites",
                brands: [
                  { name: "Squarespace", slug: "squarespace" },
                  { name: "Wix", slug: "wix" },
                ],
                cost: "~£25 / mo",
                costNote: "business plan + domain",
                body:
                  "Fed up wrestling with Squarespace or Wix? We'll build you a new website — with unlimited iterations until you're happy.",
              },
              {
                key: "integrations",
                title: "Integrations & Automations",
                brands: [{ name: "Zapier", slug: "zapier" }],
                cost: "~£30 / mo",
                costNote: "professional plan",
                body:
                  "No more Zapier holding your stack together with duct tape. Do everything in one place and save yourself the tech headaches.",
              },
            ].map((item) => (
              <div
                key={item.key}
                data-testid={`replace-card-${item.key}`}
                className="group relative bg-black p-8 md:p-10 hover:bg-[#0A0A0A] transition-colors"
              >
                <div className="flex items-start justify-between gap-4 mb-8">
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
                    {item.brands.map((b) => (
                      <BrandMark key={b.name} name={b.name} slug={b.slug} />
                    ))}
                  </div>
                  <span className="hidden md:inline-block text-xs uppercase tracking-[0.24em] text-[#D9A6F5] shrink-0 mt-2">
                    Replaced
                  </span>
                </div>
                <div className="text-xs uppercase tracking-[0.24em] text-[#D9A6F5] mb-3">
                  {item.title}
                </div>
                <p className="text-base md:text-lg text-[#A3A3A3] leading-relaxed">{item.body}</p>

                {item.cost && (
                  <div
                    data-testid={`replace-card-${item.key}-cost`}
                    className="mt-6 flex items-baseline gap-4 pt-6 border-t border-white/10"
                  >
                    <div className="relative inline-block">
                      <span className="text-2xl md:text-3xl font-black tracking-tighter text-[#F5F5F0]/70">
                        {item.cost}
                      </span>
                      <span
                        aria-hidden
                        className="absolute left-[-6px] right-[-6px] top-1/2 h-[2px] -translate-y-1/2 bg-[#D9A6F5] rotate-[-8deg] rounded-full"
                      />
                    </div>
                    <span className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3]/70">
                      {item.costNote}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="#contact"
              data-testid="replace-section-cta"
              className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-8 py-4 text-xs uppercase tracking-[0.24em] font-bold transition-colors"
            >
              Get Our Venue Info Pack <ArrowUpRight size={14} />
            </a>
            <a
              href="#book-demo"
              data-testid="replace-section-book-demo"
              className="inline-flex items-center gap-2 border border-white/25 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-8 py-4 text-xs uppercase tracking-[0.24em] font-bold transition-colors"
            >
              Book a Demo <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="relative z-[2] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="text-center mb-14 md:mb-16">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D9A6F5]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#D9A6F5]">
                What&apos;s Included
              </span>
              <span className="h-px w-10 bg-[#D9A6F5]" />
            </div>
            <h2 className="text-3xl md:text-5xl font-black tracking-tighter leading-[1] max-w-3xl mx-auto">
              Everything you need,{" "}
              <span className="font-accent text-[#D9A6F5] inline-block -rotate-2">
                out of the box.
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            {[
              {
                icon: Calendar,
                title: "Event Management",
                body:
                  "Create multiple events in minutes. Run recurring events on certain days of the week? Set them up in minutes, dictate when tickets go on sale, to who and when. Create access codes, discount codes and more to sell tickets how you want to.",
              },
              {
                icon: Ticket,
                title: "Digital Tickets",
                body:
                  "Get paid to sell tickets on Swingle. Sell digital tickets that your guests can add to their Apple Wallet or Google Wallet, payment processor fees covered and £0.20 paid to you for every ticket you sell. All powered by Stripe.",
              },
              {
                icon: IdCard,
                title: "Digital Memberships",
                body:
                  "Ditch the plastic. Sell digital memberships if your club has members — toggle this on or off as you see fit. Sell lifetime or annual memberships; your members get an automatic notification to renew a month before their membership expires. Your members get a beautiful digital membership card with your branding, no more replacing lost cards ever again. Upload your current members list and new members get added automatically.",
              },
              {
                icon: ClipboardList,
                title: "Guest Lists",
                body:
                  "All your event guests in one place, complete with their platform profiles for easy access.",
              },
              {
                icon: QrCode,
                title: "QR Scanner",
                body:
                  "Say goodbye to your clipboard. A digital QR scanner all built into your dashboard — scan and check in guests in seconds and get a full live list of who's in your club at any point in the night.",
              },
              {
                icon: Mail,
                title: "Email Automation",
                body:
                  "Bi-weekly What's On emails to your full contact list, automatic emails to specific guest lists, discount emails, new event emails and more — all without you lifting a finger. Just press send.",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  data-testid={`included-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-black p-8 md:p-10 group hover:bg-[#0A0A0A] transition-colors"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <span className="inline-flex items-center justify-center h-10 w-10 border border-[#D9A6F5]/40 text-[#D9A6F5]">
                      <Icon size={18} />
                    </span>
                    <h3 className="text-xl md:text-2xl font-black tracking-tight text-[#F5F5F0]">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-sm md:text-base text-[#A3A3A3] leading-relaxed">
                    {item.body}
                  </p>
                </div>
              );
            })}

            {/* Enterprise Website — full-width paid add-on */}
            <div
              data-testid="included-enterprise-website"
              className="md:col-span-2 bg-black p-8 md:p-10 border-t border-white/10"
            >
              <div className="flex flex-col md:flex-row md:items-start gap-6">
                <div className="flex items-center gap-3 md:min-w-[260px]">
                  <span className="inline-flex items-center justify-center h-10 w-10 border border-[#D9A6F5]/40 text-[#D9A6F5]">
                    <Globe size={18} />
                  </span>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#D9A6F5] mb-1">
                      Optional add-on
                    </div>
                    <h3 className="text-xl md:text-2xl font-black tracking-tight text-[#F5F5F0]">
                      Enterprise Website
                    </h3>
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm md:text-base text-[#A3A3A3] leading-relaxed">
                    Stop spending time on your website and start working on your business. We can
                    build you a beautiful, custom-made, no-template website. Fully integrated with
                    your Swingle venue profile.
                  </p>
                  <p className="mt-3 text-xs uppercase tracking-[0.22em] text-[#A3A3A3]">
                    <span className="text-[#F5F5F0] font-bold">£495</span> set-up ·{" "}
                    <span className="text-[#F5F5F0] font-bold">£69/mo</span> hosting · unlimited
                    changes &amp; iterations · 24/7, 365
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="#contact"
              data-testid="included-section-cta"
              className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-8 py-4 text-xs uppercase tracking-[0.24em] font-bold transition-colors"
            >
              Get Our Venue Info Pack <ArrowUpRight size={14} />
            </a>
            <a
              href="#book-demo"
              data-testid="included-section-book-demo"
              className="inline-flex items-center gap-2 border border-white/25 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-8 py-4 text-xs uppercase tracking-[0.24em] font-bold transition-colors"
            >
              Book a Demo <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section className="relative z-[2] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="flex items-center justify-center gap-3 text-xs uppercase tracking-[0.3em] text-[#D9A6F5] mb-6">
            <TrendingUp size={16} /> Savings Calculator
          </div>
          <h2 className="text-center text-3xl md:text-5xl font-black tracking-tighter leading-[1] mb-4 max-w-3xl mx-auto">
            How many tickets do you sell a month?
          </h2>
          <p className="text-center text-sm md:text-base text-[#A3A3A3] mb-12 max-w-2xl mx-auto">
            Use our handy calculator below to see how much extra we&apos;ll make you a month!
          </p>

          <div className="brackets bg-[#0A0A0A] border border-white/10 p-8 md:p-12">
            <div className="flex items-baseline justify-between mb-3">
              <span className="text-xs uppercase tracking-[0.22em] text-[#A3A3A3]">
                Tickets per month
              </span>
              <span
                data-testid="calculator-ticket-count"
                className="text-3xl md:text-4xl font-black tracking-tighter text-[#F5F5F0]"
              >
                {tickets.toLocaleString("en-GB")}
              </span>
            </div>

            <Slider
              data-testid="calculator-slider"
              value={[tickets]}
              onValueChange={(v) => setTickets(v[0])}
              min={MIN}
              max={MAX}
              step={STEP}
              className="my-6"
            />

            <div className="flex justify-between text-xs uppercase tracking-[0.22em] text-[#A3A3A3]">
              <span>{MIN.toLocaleString("en-GB")}</span>
              <span>{MAX.toLocaleString("en-GB")}+</span>
            </div>

            <div className="mt-12 border-t border-white/10 pt-8 grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#D9A6F5] mb-3">
                  We give you extra each month
                </div>
                <div
                  data-testid="calculator-monthly-result"
                  className="text-4xl md:text-5xl font-black tracking-tighter text-[#F5F5F0]"
                >
                  {formatGBP(monthly)}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#D9A6F5] mb-3">
                  We give you extra each year
                </div>
                <div
                  data-testid="calculator-annual-result"
                  className="text-4xl md:text-5xl font-black tracking-tighter text-[#F5F5F0]"
                >
                  {formatGBP(annual)}
                </div>
              </div>
            </div>

            <div className="mt-8 text-center space-y-3 text-sm md:text-base text-[#A3A3A3] leading-relaxed">
              <p>
                We cover your payment processing fees and give you{" "}
                <span className="text-[#D9A6F5] font-bold">£0.20p</span> extra for every ticket
                you sell!
              </p>
              <p className="text-[#F5F5F0] font-bold">
                You will always receive 100% of your ticket price plus{" "}
                <span className="text-[#D9A6F5]">£0.20</span>, for life!
              </p>
            </div>
          </div>

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="#contact"
              data-testid="calculator-cta"
              className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-8 py-4 text-xs uppercase tracking-[0.24em] font-bold transition-colors"
            >
              Get Our Venue Info Pack <ArrowUpRight size={14} />
            </a>
            <a
              href="#book-demo"
              data-testid="calculator-book-demo"
              className="inline-flex items-center gap-2 border border-white/25 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-8 py-4 text-xs uppercase tracking-[0.24em] font-bold transition-colors"
            >
              Book a Demo <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="relative z-[2] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="text-center mb-14 md:mb-16">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D9A6F5]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#D9A6F5]">Pricing</span>
              <span className="h-px w-10 bg-[#D9A6F5]" />
            </div>
            <h2 className="text-3xl md:text-5xl font-black tracking-tighter leading-[1] max-w-3xl mx-auto">
              Two plans.{" "}
              <span className="font-accent text-[#D9A6F5] inline-block -rotate-2">
                Zero surprises.
              </span>
            </h2>
            <p className="mt-6 text-lg text-[#A3A3A3] leading-relaxed max-w-xl mx-auto">
              No contracts, No Exit Fees, No Hidden Charges. Pick the plan that fits your venue.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            {/* Plan 1 */}
            <div
              data-testid="pricing-plan-free"
              className="bg-black p-10 md:p-12 flex flex-col"
            >
              <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-4">
                Without a website
              </div>
              <h3 className="text-3xl md:text-4xl font-black tracking-tighter mb-8 leading-tight">
                Digital Ticketing, Digital Memberships, Email Automation, Guest List Management — all of it.
              </h3>

              <div className="flex items-baseline gap-3 mb-2">
                <span
                  data-testid="pricing-plan-free-price"
                  className="text-6xl md:text-7xl font-black tracking-tighter text-[#F5F5F0]"
                >
                  Free
                </span>
              </div>
              <div className="text-sm uppercase tracking-[0.24em] text-[#A3A3A3] mb-6">
                Forever · No monthly fee
              </div>

              {/* One-off setup fee */}
              <div className="mb-10 border-t border-white/10 pt-6">
                <div className="flex items-baseline gap-3">
                  <span className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5]">
                    No setup fee
                  </span>
                </div>
                <div className="mt-2 flex items-baseline gap-3">
                  <span
                    data-testid="pricing-plan-free-setup"
                    className="text-4xl md:text-5xl font-black tracking-tighter text-[#F5F5F0]"
                  >
                    £0
                  </span>
                </div>
                <p
                  data-testid="pricing-plan-free-setup-note"
                  className="mt-2 text-sm text-[#A3A3A3]"
                >
                  Plug our platform into your existing website. Go live in days.
                </p>
              </div>

              <ul className="space-y-3 text-[#A3A3A3] mb-10">
                {[
                  "Full ticketing platform",
                  "Digital memberships & guest lists",
                  "Event scheduling & automated emails",
                  "Shared booking fee revenue — add £1,000s back into your business each year",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 border border-[#D9A6F5] items-center justify-center text-[#D9A6F5]">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    <span className="text-base">{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                data-testid="pricing-plan-free-cta"
                className="mt-auto inline-flex items-center justify-center gap-2 border border-white/25 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-6 py-4 text-xs uppercase tracking-[0.24em] font-bold transition-colors"
              >
                Get started free <ArrowUpRight size={14} />
              </a>

              <p className="mt-6 text-sm text-[#A3A3A3] leading-relaxed">
                For venues who already have a website they're happy with. Keep it — plug our
                platform straight in.
              </p>
            </div>

            {/* Plan 2 — highlighted */}
            <div
              data-testid="pricing-plan-with-website"
              className="relative bg-[#0A0A0A] p-10 md:p-12 flex flex-col border border-[#D9A6F5]/30 md:border-l-0"
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-[#D9A6F5]" />
              <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-4">
                With a website
              </div>
              <h3 className="text-3xl md:text-4xl font-black tracking-tighter mb-8 leading-tight">
                Everything above, plus a brand-new website we build for you.
              </h3>

              <div className="flex items-baseline gap-3 mb-2">
                <span
                  data-testid="pricing-plan-with-website-price"
                  className="text-6xl md:text-7xl font-black tracking-tighter text-[#F5F5F0]"
                >
                  £69
                </span>
                <span className="text-lg text-[#A3A3A3]">/ month</span>
              </div>
              <div className="text-sm uppercase tracking-[0.24em] text-[#A3A3A3] mb-6">
                Unlimited iterations
              </div>

              {/* One-off setup fee */}
              <div className="mb-10 border-t border-white/10 pt-6">
                <div className="flex items-baseline gap-3">
                  <span className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5]">
                    + one-off setup
                  </span>
                </div>
                <div className="mt-2 flex items-baseline gap-3">
                  <span
                    data-testid="pricing-plan-with-website-setup"
                    className="text-4xl md:text-5xl font-black tracking-tighter text-[#F5F5F0]"
                  >
                    £495
                  </span>
                  <span className="text-sm text-[#A3A3A3]">one-time</span>
                </div>
                <p
                  data-testid="pricing-plan-with-website-setup-note"
                  className="mt-2 text-sm text-[#A3A3A3]"
                >
                  Full custom build. No templates. Unlimited iterations.
                </p>
              </div>

              <ul className="space-y-3 text-[#A3A3A3] mb-10">
                {[
                  "Everything in the Free plan",
                  "Custom-built website for your venue",
                  "Unlimited iterations until you're happy",
                  "Self-serve dashboard for events & content",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 border border-[#D9A6F5] items-center justify-center text-[#D9A6F5]">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    <span className="text-base">{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                data-testid="pricing-plan-with-website-cta"
                className="mt-auto inline-flex items-center justify-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-6 py-4 text-xs uppercase tracking-[0.24em] font-bold transition-colors"
              >
                Get Our Venue Info Pack <ArrowUpRight size={14} />
              </a>

              <p className="mt-6 text-sm text-[#A3A3A3] leading-relaxed">
                For venues whose website is tired, dated or draining their time. We handle it end
                to end.
              </p>
            </div>
          </div>
          <CtaCluster id="pricing" />
        </div>
      </section>

      {/* BOOK A DEMO */}
      <BookDemoSection />

      {/* VENUE INFO PACK */}
      <section id="contact" className="relative z-[2] border-b border-white/10 scroll-mt-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D9A6F5]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#D9A6F5]">Free Info Pack</span>
              <span className="h-px w-10 bg-[#D9A6F5]" />
            </div>
            <h2 className="text-3xl md:text-5xl font-black tracking-tighter leading-[1]">
              Get the Venue Information Pack.
            </h2>
            <p className="mt-6 text-lg text-[#A3A3A3] leading-relaxed max-w-xl mx-auto">
              Everything you need on Swingle — pricing, our fee model, integrations, and
              how we help venues keep 100% of ticket revenue. Delivered instantly.
            </p>
          </div>

          <div className="brackets bg-[#0A0A0A] border border-white/10 p-8 md:p-12">
            {submitted ? (
              <div data-testid="landing-form-success" className="text-center py-10">
                <div className="text-xs uppercase tracking-[0.3em] text-[#D9A6F5] mb-4">Sent</div>
                <h3 className="text-3xl md:text-4xl font-black tracking-tighter mb-4">
                  Your info pack is on the way.
                </h3>
                <p className="text-[#A3A3A3] max-w-md mx-auto leading-relaxed mb-8">
                  Check your inbox in the next couple of minutes. If it hasn&apos;t arrived,
                  it&apos;s worth a quick look in your spam folder just in case.
                </p>
                <button
                  type="button"
                  data-testid="landing-form-send-another"
                  onClick={() => setSubmitted(false)}
                  className="inline-flex items-center gap-2 border border-white/20 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
                >
                  Send To Another Address
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="landing-info-pack-form" className="space-y-6" noValidate>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field id="venue_name" label="Venue name" required>
                    <Input
                      id="venue_name"
                      data-testid="landing-input-venue-name"
                      value={form.venue_name}
                      onChange={update("venue_name")}
                      placeholder="The Basement, Bristol"
                      required
                      className={inputCls}
                    />
                  </Field>
                  <Field id="venue_website" label="Venue website">
                    <Input
                      id="venue_website"
                      type="url"
                      data-testid="landing-input-venue-website"
                      value={form.venue_website}
                      onChange={update("venue_website")}
                      placeholder="https://yourvenue.co.uk"
                      className={inputCls}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field id="first_name" label="First name" required>
                    <Input
                      id="first_name"
                      data-testid="landing-input-first-name"
                      value={form.first_name}
                      onChange={update("first_name")}
                      placeholder="Alex"
                      required
                      className={inputCls}
                    />
                  </Field>
                  <Field id="last_name" label="Last name" required>
                    <Input
                      id="last_name"
                      data-testid="landing-input-last-name"
                      value={form.last_name}
                      onChange={update("last_name")}
                      placeholder="Bennett"
                      required
                      className={inputCls}
                    />
                  </Field>
                </div>

                <Field id="email" label="Email address for delivery" required>
                  <Input
                    id="email"
                    type="email"
                    data-testid="landing-input-email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@yourvenue.co.uk"
                    required
                    className={inputCls}
                  />
                </Field>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="landing-form-submit"
                  className="w-full inline-flex items-center justify-center gap-3 bg-[#D9A6F5] text-black px-8 py-5 text-sm uppercase tracking-[0.22em] font-bold hover:bg-[#E8C8FF] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send Me The Info Pack <Mail size={18} />
                    </>
                  )}
                </button>

                <p className="text-xs text-[#A3A3A3] uppercase tracking-[0.2em] text-center">
                  Delivered to your inbox in minutes. No spam.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* MINIMAL FOOTER */}
      <footer className="relative z-[2] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#A3A3A3] uppercase tracking-[0.18em]">
          <p>© {new Date().getFullYear()} Swingle For Venues. UK-based, lifestyle-first.</p>
          <p>
            The only supplier that <span className="text-[#D9A6F5]">guarantees</span> to make you
            money.
          </p>
          <div className="flex items-center gap-5">
            <Link
              to="/about"
              data-testid="landing-about-link"
              className="text-[#A3A3A3]/80 hover:text-[#D9A6F5] transition-colors"
            >
              About
            </Link>
            <Link
              to="/terms"
              data-testid="landing-terms-link"
              className="text-[#A3A3A3]/80 hover:text-[#D9A6F5] transition-colors"
            >
              Terms
            </Link>
            <Link
              to="/privacy"
              data-testid="landing-privacy-link"
              className="text-[#A3A3A3]/80 hover:text-[#D9A6F5] transition-colors"
            >
              Privacy
            </Link>
            <Link
              to="/admin/login"
              data-testid="landing-admin-login-link"
              className="inline-flex items-center gap-1.5 text-[#A3A3A3]/70 hover:text-[#D9A6F5] transition-colors"
            >
              <Lock size={12} /> Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

const inputCls =
  "bg-black border-white/15 rounded-none h-12 text-[#F5F5F0] placeholder:text-[#A3A3A3]/60 focus-visible:ring-[#D9A6F5] focus-visible:ring-offset-0";

function CtaCluster({ id }) {
  return (
    <div
      data-testid={`cta-cluster-${id}`}
      className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-3"
    >
      <a
        href="#contact"
        className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-8 py-4 text-xs uppercase tracking-[0.24em] font-bold transition-colors"
      >
        Get Our Venue Info Pack <ArrowUpRight size={14} />
      </a>
      <a
        href="#book-demo"
        className="inline-flex items-center gap-2 border border-white/25 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-8 py-4 text-xs uppercase tracking-[0.24em] font-bold transition-colors"
      >
        Book a Demo <ArrowUpRight size={14} />
      </a>
    </div>
  );
}


function Field({ id, label, required, children }) {
  return (
    <div className="space-y-2 text-left">
      <Label
        htmlFor={id}
        className="text-xs uppercase tracking-[0.22em] text-[#A3A3A3] font-medium"
      >
        {label}
        {required && <span className="text-[#D9A6F5] ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}

function BrandMark({ name, slug }) {
  // slug present → use Simple Icons CDN (monochrome, cream tint for our dark theme)
  // slug null (or load fails) → styled wordmark fallback
  const [failed, setFailed] = useState(false);
  const showIcon = slug && !failed;

  return (
    <div
      className="relative inline-flex items-center h-8 md:h-9"
      title={`${name} (replaced)`}
      aria-label={`${name} (replaced)`}
    >
      {showIcon ? (
        <img
          src={`https://cdn.simpleicons.org/${slug}/F5F5F0`}
          alt={name}
          onError={() => setFailed(true)}
          className="h-full w-auto object-contain opacity-80"
          style={{ maxWidth: "160px" }}
        />
      ) : (
        <span className="text-base md:text-lg font-bold tracking-tight text-[#F5F5F0]/85">
          {name}
        </span>
      )}
      {/* Diagonal strike-through */}
      <span
        aria-hidden
        className="absolute left-[-6px] right-[-6px] top-1/2 h-[2px] -translate-y-1/2 bg-[#D9A6F5] rotate-[-8deg] rounded-full"
      />
    </div>
  );
}

